import { useMemo, useState } from "react";
import {
  Button,
  Caption1,
  Field,
  Input,
  Link,
  MessageBar,
  MessageBarBody,
  Select,
  Spinner,
  Tab,
  TabList,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";
import { ArrowDownload20Regular, CloudArrowUp20Regular, FolderOpen16Regular } from "@fluentui/react-icons";
import { Repository, TIPI_ENTE, VALUTAZIONI, type TipoEnte, type Valutazione } from "@app/schema";
import { DocumentLibrary } from "@app/sp-data";
import { useData } from "../data/DataProvider";
import { ESITI_REPORT, filtraRighe, loadDatiReport, raggruppa, riepilogo, righeReport, type EsitoReport, type FiltriReport, type Raggruppamento } from "../data/report";
import { creaExcel, scarica } from "../data/excel";
import { useAsync } from "../lib/useAsync";
import { formatDate, formatEuro } from "../lib/format";
import { goBack, navigate } from "../lib/nav";
import { Shell } from "../components/Shell";
import { Codice } from "../components/Codice";
import { Section } from "../components/Section";
import { ErrorState, describeError } from "../components/ErrorState";

const useStyles = makeStyles({
  filters: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: tokens.spacingHorizontalM, alignItems: "end" },
  tiles: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: tokens.spacingHorizontalM },
  tile: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    padding: tokens.spacingHorizontalM,
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  sel: { minWidth: 0, width: "100%" },
  nowrap: { whiteSpace: "nowrap" },
  numSmall: { fontSize: tokens.fontSizeBase400, lineHeight: tokens.lineHeightBase600, overflowWrap: "anywhere" },
  num: { fontSize: tokens.fontSizeBase600, fontWeight: tokens.fontWeightSemibold, fontVariantNumeric: "tabular-nums", lineHeight: tokens.lineHeightBase600 },
  label: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  ok: { color: tokens.colorPaletteGreenForeground1 },
  ko: { color: tokens.colorPaletteRedForeground1 },
  tableWrap: { overflowX: "auto", border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium },
  n: { textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
  head: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: tokens.spacingHorizontalM, flexWrap: "wrap" },
  actions: { display: "flex", gap: tokens.spacingHorizontalS, flexWrap: "wrap" },
  row: { cursor: "pointer" },
  sub: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
});

const anno = new Date().getFullYear();
const pct = (x?: number) => (x == null ? "—" : `${(x * 100).toLocaleString("it-IT", { maximumFractionDigits: 1 })}%`);
const dataIt = (d?: string) => (d ? d.split("-").reverse().join("/") : "");

/** Report per gestori e collaboratori: riepilogo degli esiti ed estrazione filtrata. */
export function Report() {
  const s = useStyles();
  const data = useData();
  const dati = useAsync(() => loadDatiReport(data), [data]);
  const [f, setF] = useState<FiltriReport>({ dal: `${anno}-01-01`, al: `${anno}-12-31` });
  const [per, setPer] = useState<Raggruppamento>("servizio");
  const [busy, setBusy] = useState<"scarica" | "salva">();
  const [msg, setMsg] = useState<{ intent: "success" | "error"; text: string; url?: string }>();
  const set = (patch: Partial<FiltriReport>) => setF((x) => ({ ...x, ...patch }));

  const tutte = useMemo(() => (dati.data ? righeReport(dati.data.gare, dati.data.lotti, dati.data.servizi) : undefined), [dati.data]);
  const righe = useMemo(() => (tutte ? filtraRighe(tutte, f).sort((a, b) => (a.scadenza ?? "").localeCompare(b.scadenza ?? "") || a.codice.localeCompare(b.codice) || (a.lottoNumero ?? 0) - (b.lottoNumero ?? 0)) : undefined), [tutte, f]);
  const tot = useMemo(() => (righe ? riepilogo(righe) : undefined), [righe]);
  const gruppi = useMemo(() => (righe ? raggruppa(righe, per) : []), [righe, per]);
  const servizi = (dati.data?.servizi ?? []).slice().sort((a, b) => (a.fields.Sigla ?? "").localeCompare(b.fields.Sigla ?? ""));

  /** Descrizione leggibile dei filtri, riportata nel file Excel. */
  function descrizione(): string[] {
    const sv = servizi.find((x) => x.id === f.servizio);
    return [
      `Scadenza: ${f.dal ? `dal ${dataIt(f.dal)}` : ""}${f.al ? ` al ${dataIt(f.al)}` : ""}${!f.dal && !f.al ? "qualsiasi" : ""}`,
      `Servizio: ${sv ? `${sv.fields.Sigla} – ${sv.fields.Title}` : "tutti"}`,
      `Tipo di ente: ${f.tipoEnte ?? "tutti"}`,
      `Valutazione: ${f.valutazione ?? "tutte"}`,
      `Esito: ${f.esito ?? "tutti"}`,
    ];
  }
  const nomeFile = () => `report-gare_${new Date().toISOString().slice(0, 10)}_${new Date().toTimeString().slice(0, 5).replace(":", "")}.xlsx`;

  async function esporta(dove: "scarica" | "salva") {
    if (!righe) return;
    setBusy(dove);
    setMsg(undefined);
    try {
      const blob = await creaExcel(righe, descrizione());
      const nome = nomeFile();
      if (dove === "scarica") scarica(blob, nome);
      else {
        const lib = new DocumentLibrary(data.graph, data.siteId, Repository);
        await lib.ensureFolder("Report");
        const item = await lib.upload(`Report/${nome}`, blob, { conflict: "rename" });
        setMsg({ intent: "success", text: `Salvato in Repository/Report: ${item.name}`, url: item.webUrl });
      }
    } catch (e) {
      setMsg({ intent: "error", text: describeError(e).detail });
    } finally {
      setBusy(undefined);
    }
  }

  return (
    <Shell title="Report" onBack={() => goBack()}>
      <Section title="Filtri">
        <div className={s.filters}>
          <Field label="Scadenza dal">
            <Input type="date" value={f.dal ?? ""} onChange={(_, d) => set({ dal: d.value || undefined })} />
          </Field>
          <Field label="al">
            <Input type="date" value={f.al ?? ""} onChange={(_, d) => set({ al: d.value || undefined })} />
          </Field>
          <Field label="Servizio">
            <Select className={s.sel} value={f.servizio ? String(f.servizio) : ""} onChange={(_, d) => set({ servizio: d.value ? Number(d.value) : undefined })}>
              <option value="">Tutti</option>
              {servizi.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.fields.Sigla} – {x.fields.Title}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Tipo di ente">
            <Select className={s.sel} value={f.tipoEnte ?? ""} onChange={(_, d) => set({ tipoEnte: (d.value || undefined) as TipoEnte | undefined })}>
              <option value="">Tutti</option>
              {TIPI_ENTE.map((x) => (
                <option key={x}>{x}</option>
              ))}
            </Select>
          </Field>
          <Field label="Valutazione">
            <Select className={s.sel} value={f.valutazione ?? ""} onChange={(_, d) => set({ valutazione: (d.value || undefined) as Valutazione | undefined })}>
              <option value="">Tutte</option>
              {VALUTAZIONI.map((x) => (
                <option key={x}>{x}</option>
              ))}
            </Select>
          </Field>
          <Field label="Esito">
            <Select className={s.sel} value={f.esito ?? ""} onChange={(_, d) => set({ esito: (d.value || undefined) as EsitoReport | undefined })}>
              <option value="">Tutti</option>
              {ESITI_REPORT.map((x) => (
                <option key={x}>{x}</option>
              ))}
            </Select>
          </Field>
        </div>
        <Caption1 className={s.sub}>
          Il periodo si applica alla data di scadenza. Comprende le gare in corso e quelle archiviate. Gli esiti si contano per partecipazione: la gara senza lotti, oppure ciascun lotto.
        </Caption1>
      </Section>

      {dati.error ? (
        <ErrorState error={dati.error} />
      ) : !righe || !tot ? (
        <Spinner style={{ marginTop: 48 }} label="Lettura delle gare…" />
      ) : (
        <>
          <div className={s.tiles}>
            <Tile n={tot.gare} label="Gare" />
            <Tile n={tot.partecipate} label="Partecipate" />
            <Tile n={tot.nonPartecipate} label="Non partecipate" />
            <Tile n={tot.aggiudicate} label="Aggiudicate" cls={s.ok} />
            <Tile n={tot.nonAggiudicate} label="Non aggiudicate" cls={s.ko} />
            <Tile n={tot.inAttesa} label="In attesa di esito" />
            <Tile n={pct(tot.tasso)} label="Tasso di aggiudicazione" />
            <Tile n={formatEuro(tot.importoAggiudicato)} label="Importo aggiudicato" cls={s.numSmall} />
          </div>

          <Section>
            <div className={s.head}>
              <TabList selectedValue={per} onTabSelect={(_, d) => setPer(d.value as Raggruppamento)}>
                <Tab value="servizio">Per servizio</Tab>
                <Tab value="tipoEnte">Per tipo di ente</Tab>
                <Tab value="anno">Per anno</Tab>
              </TabList>
            </div>
            <div className={s.tableWrap}>
              <Table size="small" aria-label="Riepilogo" style={{ tableLayout: "auto" }}>
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell>{per === "servizio" ? "Servizio" : per === "tipoEnte" ? "Tipo di ente" : "Anno di scadenza"}</TableHeaderCell>
                    <TableHeaderCell className={s.n}>Gare</TableHeaderCell>
                    <TableHeaderCell className={s.n}>Partecipazioni</TableHeaderCell>
                    <TableHeaderCell className={s.n}>Aggiudicate</TableHeaderCell>
                    <TableHeaderCell className={s.n}>Non aggiudicate</TableHeaderCell>
                    <TableHeaderCell className={s.n}>In attesa</TableHeaderCell>
                    <TableHeaderCell className={s.n}>Tasso</TableHeaderCell>
                    <TableHeaderCell className={s.n}>Base d'asta</TableHeaderCell>
                    <TableHeaderCell className={s.n}>Aggiudicato</TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {gruppi.map((g) => (
                    <TableRow key={g.chiave}>
                      <TableCell>{g.chiave}</TableCell>
                      <TableCell className={s.n}>{g.riepilogo.gare}</TableCell>
                      <TableCell className={s.n}>{g.riepilogo.partecipazioni}</TableCell>
                      <TableCell className={s.n}>{g.riepilogo.aggiudicate}</TableCell>
                      <TableCell className={s.n}>{g.riepilogo.nonAggiudicate}</TableCell>
                      <TableCell className={s.n}>{g.riepilogo.inAttesa}</TableCell>
                      <TableCell className={s.n}>{pct(g.riepilogo.tasso)}</TableCell>
                      <TableCell className={s.n}>{formatEuro(g.riepilogo.importoPartecipato)}</TableCell>
                      <TableCell className={s.n}>{formatEuro(g.riepilogo.importoAggiudicato)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Section>

          <Section>
            <div className={s.head}>
              <span>
                <strong>Estrazione</strong> <span className={s.sub}>· {righe.length} righe</span>
              </span>
              <div className={s.actions}>
                <Button icon={<ArrowDownload20Regular />} onClick={() => esporta("scarica")} disabled={!!busy || !righe.length}>
                  {busy === "scarica" ? "Preparazione…" : "Scarica Excel"}
                </Button>
                <Button icon={<CloudArrowUp20Regular />} onClick={() => esporta("salva")} disabled={!!busy || !righe.length}>
                  {busy === "salva" ? "Salvataggio…" : "Salva in SharePoint"}
                </Button>
              </div>
            </div>
            {msg && (
              <MessageBar intent={msg.intent}>
                <MessageBarBody>
                  {msg.text}{" "}
                  {msg.url && (
                    <Link href={msg.url} target="_blank" rel="noreferrer">
                      Apri
                    </Link>
                  )}
                </MessageBarBody>
              </MessageBar>
            )}
            <div className={s.tableWrap}>
              <Table size="small" aria-label="Estrazione" style={{ tableLayout: "auto", minWidth: 1100 }}>
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell>Codice</TableHeaderCell>
                    <TableHeaderCell style={{ width: 50 }}>Lotto</TableHeaderCell>
                    <TableHeaderCell style={{ minWidth: 320 }}>Oggetto e stazione appaltante</TableHeaderCell>
                    <TableHeaderCell style={{ width: 140 }}>Tipo di ente</TableHeaderCell>
                    <TableHeaderCell style={{ width: 60 }}>Sigla</TableHeaderCell>
                    <TableHeaderCell style={{ width: 140 }}>Scadenza</TableHeaderCell>
                    <TableHeaderCell className={s.n} style={{ width: 120 }}>
                      Base d'asta
                    </TableHeaderCell>
                    <TableHeaderCell style={{ width: 110 }}>Valutazione</TableHeaderCell>
                    <TableHeaderCell style={{ width: 120 }}>Esito</TableHeaderCell>
                    <TableHeaderCell className={s.n} style={{ width: 120 }}>
                      Aggiudicato
                    </TableHeaderCell>
                    <TableHeaderCell style={{ width: 40 }} aria-label="Cartella" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {righe.map((r) => (
                    <TableRow key={`${r.gara.id}-${r.lottoNumero ?? 0}`} className={s.row} onClick={() => navigate(`/g/${r.gara.id}`)}>
                      <TableCell>
                        <Codice value={r.codice} />
                      </TableCell>
                      <TableCell>{r.lottoNumero ? `L${r.lottoNumero}` : ""}</TableCell>
                      <TableCell>
                        {r.oggetto}
                        <div className={s.sub}>{r.stazione}</div>
                      </TableCell>
                      <TableCell>{r.tipoEnte ?? ""}</TableCell>
                      <TableCell>{r.servizio?.fields.Sigla}</TableCell>
                      <TableCell className={s.nowrap}>{r.scadenza ? formatDate(r.scadenza) : ""}</TableCell>
                      <TableCell className={s.n}>{r.importo != null ? formatEuro(r.importo) : ""}</TableCell>
                      <TableCell className={s.nowrap}>{r.valutazione}</TableCell>
                      <TableCell className={mergeClasses(s.nowrap, r.esito === "Aggiudicata" && s.ok, r.esito === "Non aggiudicata" && s.ko)}>{r.esito ?? ""}</TableCell>
                      <TableCell className={s.n}>{r.importoAggiudicato != null ? formatEuro(r.importoAggiudicato) : ""}</TableCell>
                      <TableCell>
                        {r.cartellaUrl && (
                          <Link href={r.cartellaUrl} target="_blank" rel="noreferrer" aria-label="Apri la cartella" onClick={(e) => e.stopPropagation()}>
                            <FolderOpen16Regular />
                          </Link>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Section>
        </>
      )}
    </Shell>
  );
}

function Tile({ n, label, cls }: { n: number | string; label: string; cls?: string }) {
  const s = useStyles();
  return (
    <div className={s.tile}>
      <span className={mergeClasses(s.num, cls)}>{n}</span>
      <span className={s.label}>{label}</span>
    </div>
  );
}
